import { buildSeoCard } from "~/review/seo-card";
import { loadClamp } from "~/es6/clamp-loader";
import FirmReviewEdit from "~/review/edit";
import FirmReviewItem from "~/review/item";
import FirmStarsReview from "~/review/stars-review";
import { CONFIG } from "~/config";
import { dateFormat } from "~/util/format";
import { node, icon, isInViewport } from "~/util/dom";
import SortBox from "~/ui/sortbox";
import { REVIEWS_LIST_SORT_OPTIONS } from "~/review/const";
import { getBadgeComposedComponent, getFirmLayout, reviewGetBadgeColor, reviewHasColoredBadge } from "~/util/review";
import { listReviewsPanel } from "~/util/providers/review";
import { IReviewConf } from "~/interfaces";
import { IReview } from "~/review/interfaces";
import { _ } from "~/i18n";
import { createEmojisPreview } from "~/ui/review/emojisPreview";
import { store } from "~/state/store";
import { useScroll } from "~/util/usescroll";
import Notifications, { NOTIFICATIONS_TYPE } from "~/util/notifications";
import { makeEvent } from "~/lib/signals";
import ReviewPopup from "~/review/popupwc";

type TReviewsListComp = { deactivate: () => void };

export default class ReviewsList {
	_dom: {
		backBtnLink: HTMLAnchorElement;
		[key: string]: HTMLElement;
	};

	_reviewsListComp: TReviewsListComp;

	_id: number;

	_data: IReviewConf;

	_items: Array<FirmReviewItem>;

	_offset: number;

	_loadMore: boolean;

	_scrollLoad: boolean;

	_timeout: {
		handler: ReturnType<typeof setTimeout>;
		time: number;
	};

	_sortValue: string;

	_loading: boolean;

	_logSource: string;

	_load20: boolean;

	_noAnalyticsForThree: boolean;

	_seoCard: HTMLScriptElement;

	_firmReviewEdit: FirmReviewEdit;

	_sortBox: SortBox;

	_scrollListener: ReturnType<typeof useScroll>;

	constructor(reviewsListComp: TReviewsListComp) {
		const layoutComp = Mapy.getComponent("layout");

		this._reviewsListComp = reviewsListComp;
		this._dom = {
			container: node("div", { id: "reviewsList" }),
			header: node("div", { className: "header" }),
			list: node("div", { className: "list firm-content" }),
			caption: node("h1", { className: "caption", textContent: _("detail.content.review2.allReviews") }),
			poiTitle: node("h2", { className: "poi-title", textContent: "" }),
			backBtnLink: node("a", { className: "back-btn-link" }),
			reviewLine: node("div", { className: "review-line" }),
			structured: node("div", { className: "structured-area" }),
			reviewContent: node("div", { className: "review-content" }),
			reviewsContent: node("div", { className: "reviews-content" }),
			filterContent: node("div", { className: "filter-content" }),
			loader: node("p", { className: "loading" }),
			layoutEl: layoutComp.getScrollNode(),
			layoutBodyEl: layoutComp.getBody(),
			layoutHeaderEl: layoutComp.getHeader(),
		};
		this._dom.container.appendChild(this._dom.header);
		this._dom.container.appendChild(this._dom.list);
		this._dom.header.appendChild(this._dom.caption);
		this._dom.header.appendChild(this._dom.poiTitle);
		this._dom.header.appendChild(this._dom.backBtnLink);
		this._dom.header.appendChild(this._dom.reviewLine);
		this._dom.header.appendChild(this._dom.structured);
		this._dom.list.appendChild(this._dom.reviewContent);
		this._dom.list.appendChild(this._dom.filterContent);
		this._dom.list.appendChild(this._dom.reviewsContent);

		const backBtn = node("button", { className: "back-btn" });

		backBtn.appendChild(icon("close"));
		this._dom.backBtnLink.appendChild(backBtn);
		this._dom.backBtnLink.addEventListener("click", event => {
			event.preventDefault();
			event.stopPropagation();
			this._reviewsListComp.deactivate();
		});
		this._id = null;
		this._data = null;
		this._items = [];
		this._offset = 0;
		this._loadMore = true;
		this._scrollLoad = false;
		this._timeout = {
			handler: null,
			time: 50,
		};
		this._sortValue = "";
		this._loading = false;
		this._logSource = "recenze-panel";
		this._load20 = false;
		this._noAnalyticsForThree = false;
		this._seoCard = null;
		this._firmReviewEdit = null;
		this._sortBox = null;
		this._scrollListener = null;
	}

	get container() {
		return this._dom.container;
	}

	activate() {
		this._scrollListener = useScroll({
			onScroll: (isBottom: boolean) => {
				if (isBottom && !this._loading) {
					this._loadReviews();
				}

				this._myReviewImpress();
				this._itemsImpress();
			},
		});

		const { filter, sortBox } = this._buildFilter();

		this._dom.filterContent.innerHTML = "";
		this._sortBox = sortBox;

		this._dom.filterContent.append(filter);
		window.addEventListener("review-report", this);
		makeEvent("reviewslist-activated", this);
	}

	deactivate() {
		while (this._items.length) {
			const item = this._items.pop();

			item.destroy();
		}

		this._destroySortBox();
		this._scrollListener && this._scrollListener();
		this._scrollListener = null;

		this._loadMore = true;
		this._offset = 0;
		this._sortValue = "";
		this._loading = false;
		this._scrollLoad = false;
		this._dom.reviewLine.innerHTML = "";
		this._dom.structured.innerHTML = "";
		this._dom.reviewsContent.innerHTML = "";
		this._dom.filterContent.innerHTML = "";
		this._load20 = false;
		this._seoCard?.remove();
		this._seoCard = null;
		window.removeEventListener("review-report", this);
		makeEvent("reviewslist-deactivated", this);
	}

	handleEvent(event: any) {
		if (event.type === "review-report") {
			const { reviewId }: { reviewId: number } = event.detail;

			// smazeme
			this._items.forEach(item => {
				if (item.data.id === reviewId) {
					item.destroy();
				}
			});
			// aktualizujeme
			this._setReview();
		}
	}

	removeMyReview(newReview: IReview) {
		const alternatives = this._data.review.alternatives;

		this._data.review = {
			...newReview,
			...alternatives ? { alternatives } : {},
		};
		this._data.myReview = null;

		// aktualizace hodnoceni
		this._setReview();

		while (this._items.length) {
			const item = this._items.pop();

			item.destroy();
		}

		// nacteme recenze
		this._loadMore = true;
		this._offset = 0;

		this._loadReviews();
	}

	/* eslint-disable class-methods-use-this */
	getState() {
		return {};
	}

	setState(state) {
		if (state.id) {
			this._id = state.id;
		}

		this._load20 = state.load20;
		this._noAnalyticsForThree = state.noAnalyticsForThree;

		if (state.poiData) {
			this._data = state.poiData;
			// odkaz na zpet
			this._dom.backBtnLink.href = `https://${CONFIG.BASE_URL}/?source=${state.poiData.source}&id=${state.poiData.id}`;
			// seo
			Mapy.SEO.set(`Recenze - ${state.poiData.title}`, state.poiData.desc);
			// hodnoceni
			this._setReview();
			// nacteme recenzi
			this._loadReviews();
			// hlavicka
			this._dom.poiTitle.textContent = state.poiData.title;
		}
	}

	_loadReviews() {
		if (!this._loadMore || this._loading) {
			return;
		}

		this._dom.reviewsContent.appendChild(this._dom.loader);
		this._loading = true;

		listReviewsPanel({
			source: this._data.source,
			id: this._id,
			offset: this._offset,
			order: this._sortValue,
			userId: this._data.myReview ? this._data.myReview.id : 0,
			seoLoad: this._load20,
		}).then(data => {
			this._dom.loader.remove();

			// vytvorime polozky
			data.reviews.forEach((reviewData, ind) => {
				const item = new FirmReviewItem({
					data: reviewData,
					isFirm: this._data.isFirm,
					isTwin: this._data.isTwin,
					index: this._offset + ind + 1 + (this._data.myReview ? 1 : 0),
					commentsSync: this._offset === 0 && ind < 5, // dame si to o 1 vic pro vsechny pripady
					isOwner: this._data.rusAdmin && !this._data.inactive,
					isDetailInactive: this._data.inactive,
					logSource: this._logSource,
					onOpen: () => {
						this._items.forEach(optsItem => optsItem.closeOpts());
					},
					analytics: {
						sourcePoi: this._data.source,
						typeid: this._data.typeId,
						firmTwin: this._data.isTwin ? 1 : 0,
					},
				});

				this._items.push(item);
				this._dom.reviewsContent.appendChild(item.container);

				if (!data.loadMore && ind === data.reviews.length - 1) {
					item.container.classList.add("no-line");
				}
			});

			loadClamp().then(() => {
				this._items.forEach(item => item.clamp());
			}, () => { /**/ });

			this._offset = data.offset;
			this._loadMore = data.loadMore;
			this._loading = false;

			if (this._loadMore && !this._scrollLoad && this._dom.layoutBodyEl.offsetHeight < this._dom.layoutEl.offsetHeight) {
				this._scrollLoad = true;
				this._loadReviews();
			}

			const detailData = store.getState().detail;

			if (!this._seoCard && this._data.review && detailData.data && detailData.data.extend) {
				this._seoCard = buildSeoCard(detailData, this._data.review, data.reviews);
				document.head.appendChild(this._seoCard);
			}

			this._load20 = false;
			this._scrollLoad = true;

			this._myReviewImpress();
			this._itemsImpress();
		}, () => {
			this._dom.loader.remove();
			this._loading = false;
		});
	}

	_setReview(openEdit?: boolean) {
		// hodnoceni poie
		this._dom.reviewLine.innerHTML = "";
		this._dom.structured.innerHTML = "";
		this._firmReviewEdit = null;

		let tooltipMsg = `${_("detail.content.review2.seznamReviewNotice")}`;

		this._dom.reviewLine.appendChild(node(getBadgeComposedComponent(), {
			type: reviewGetBadgeColor(this._data.review),
			size: "big",
			rating: this._data.review.review_rating_stars,
			text: this._data.review.review_rating_caption,
			reviews: this._data.review.total,
			lang: CONFIG.defaultLang,
		}));

		if (reviewHasColoredBadge(this._data.review)) {
			tooltipMsg = `${_("detail.content.review2.bubbleInfo2")}<br /><br />${_("detail.content.review2.seznamReviewNotice")}`;
		}

		if (this._data.manageMyCompany) {
			const link = node("a", { href: this._data.manageMyCompany, className: "managemycompany-link", target: "_blank", textContent: _("detail.content.review2.manageOwnCompany") });

			this._dom.reviewLine.before(link);
		}

		// pridani tooltipu
		const trigger = node("span", { className: "trigger" });
		const tooltipList = node("p", { className: "bubble", innerHTML: tooltipMsg });

		trigger.appendChild(icon("square-info"));
		trigger.append(tooltipList);
		this._dom.reviewLine.append(trigger);

		const structuredData = createEmojisPreview({
			config: this._data.structuredRating,
		});

		if (!structuredData.isHidden) {
			this._dom.structured.append(structuredData.container);
		}

		if (this._data.rusAdmin) {
			return;
		}

		// vlastni hodnoceni
		this._dom.reviewContent.innerHTML = "";

		if (this._data.myReview) {
			const firmReviewEdit = new FirmReviewEdit({
				data: this._data,
				inactive: this._data.inactive,
				logSource: this._logSource,
				isPanel: true,
				onSave: putReviewData => {
					if (putReviewData.review) {
						const alternatives = this._data.review.alternatives;

						this._data.review = {
							...putReviewData.review,
							...alternatives ? { alternatives } : {},
						};
					}

					this._data.myReview = putReviewData.myReview;
					// upravime se sami
					this._setReview();
					// posleme event
					this._callEvent();
				},
			});

			this._dom.reviewContent.appendChild(firmReviewEdit.container);
			// odpoved na vlastni hodnoceni
			if (this._data.myReview.reply && this._data.myReview.reply.text) {
				this._dom.reviewContent.appendChild(this._buildReply());
			}

			if (openEdit) {
				firmReviewEdit.openEdit();
			}

			this._firmReviewEdit = firmReviewEdit;

			this._myReviewImpress();
		} else if (!this._data.inactive) {
			const starsReview = new FirmStarsReview({
				data: this._data,
				logSource: this._logSource,
				logPosition: "top",
				onLoginError: data => {
					if (data.isOwner) {
						this._data.rusAdmin = true;
						starsReview.destroy();
						Notifications.notify({
							content: _("detail.content.review2.errorOwner"),
							type: NOTIFICATIONS_TYPE.ERROR,
						});
						this._setReview();
						this._callEvent();
					} else if (data.myReview) {
						this._data.myReview = data.myReview;
						starsReview.destroy();
						this._setReview(true);
						this._callEvent();
					}
				},
				onError: () => {
					this._log("reviews_fail_stars");
				},
				showPopupCb: putReviewData => {
					// ulozime
					if (putReviewData.review) {
						const alternatives = this._data.review.alternatives;

						this._data.review = {
							...putReviewData.review,
							...alternatives ? { alternatives } : {},
						};
					}

					this._data.myReview = putReviewData.myReview;

					this._createReviewPopup(true);
				},
				onStructuredRating: data => {
					this._data.id = data.id;
					this._data.source = data.source;
					this._data.review = data.review;

					this._createReviewPopup(false, data.rating);
				},
			});

			this._dom.reviewContent.appendChild(starsReview.container);
		}
	}

	_createReviewPopup(textReview: boolean, forceRating = 0) {
		// vlozeni recenze
		/* eslint-disable no-new */
		new ReviewPopup({
			data: this._data,
			textReview,
			logPosition: "top",
			forceRating,
			logSource: this._logSource,
			onClose: closeData => {
				if (closeData.review) {
					const alternatives = this._data.review.alternatives;

					this._data.review = {
						...closeData.review,
						...alternatives ? { alternatives } : {},
					};
				}

				this._data.myReview = closeData.myReview;
				// upravime se sami
				this._setReview();
				// posleme event
				this._callEvent();
			},
		});
	}

	_callEvent() {
		// zavolame callback pro comments a review
		window.dispatchEvent(new CustomEvent("firmreview-review-myreview", {
			detail: {
				rusAdmin: this._data.rusAdmin,
				review: this._data.review,
				myReview: this._data.myReview,
			},
		}));
		window.dispatchEvent(new CustomEvent("firmreview-comments-myreview", {
			detail: {
				rusAdmin: this._data.rusAdmin,
				review: this._data.review,
				myReview: this._data.myReview,
			},
		}));

		// data mezi taby
		const { data } = store.getState().detail;

		Mapy.getComponent("detail").getBroadCast()?.postMessage(JSON.stringify({
			type: "firm-review",
			data: {
				rusAdmin: this._data.rusAdmin,
				review: this._data.review,
				myReview: this._data.myReview,
				source: data?.source,
				id: data?.id,
			},
		}));
	}

	_buildFilter() {
		const filter = node("div", { className: "filter" });
		const titleReviews = node("h2", { textContent: _("detail.content.review2.otherReviews"), className: "title-reviews" });
		const sortBox = new SortBox({
			options: REVIEWS_LIST_SORT_OPTIONS,
			onSelect: value => {
				Mapy.getComponent("logger").log("click", {
					text: value || "default",
					section: "reviews",
					source: "recenze-panel",
				});

				this._sortValue = value;
				this._offset = 0;
				this._loadMore = true;

				while (this._items.length) {
					const item = this._items.pop();

					item.destroy();
				}

				this._loadReviews();
			},
			onOpen: () => {
				Mapy.getComponent("logger").log("click", {
					text: "reviewsSortingShow",
					section: "reviews",
					source: "recenze-panel",
				});
			},
			selectedIndex: 0,
		});

		filter.append(titleReviews);
		filter.append(sortBox.node);

		return {
			filter,
			sortBox,
		};
	}

	_buildReply() {
		const data = this._data.myReview.reply;
		const replyCont = node("div", { className: "myreview-reply-cont" });
		// vytvorime
		const replyInner = node("div", { className: "reply-inner" });
		const circle = node("span", { className: "circle" });

		circle.appendChild(icon("reply"));
		replyInner.appendChild(circle);

		const caption = node("h2", { className: "reply-title", textContent: _("detail.content.review2.reaction") });
		const replyDesc = node("p", { textContent: `${data.text}` });
		// datum
		const date = node("span", { className: `date`, textContent: dateFormat(new Date(data.updated), "d. mn yyyy") });

		replyInner.appendChild(caption);
		replyInner.appendChild(replyDesc);
		caption.appendChild(date);
		replyCont.appendChild(replyInner);

		return replyCont;
	}

	_myReviewImpress() {
		if (this._firmReviewEdit && !this._noAnalyticsForThree && isInViewport(this._firmReviewEdit.header)) {
			this._firmReviewEdit.firmImpressAnalytics();
		}
	}

	_itemsImpress() {
		this._items.forEach((item, ind) => {
			if (this._noAnalyticsForThree && ind <= 2) {
				return;
			}

			if (isInViewport(item.header)) {
				item.firmImpressAnalytics();
			}
		});
	}

	_destroySortBox() {
		if (this._sortBox) {
			this._sortBox.destroy();
			this._sortBox = null;
		}
	}

	_log(name: string, data?: object) {
		// analytika
		/* eslint-disable camelcase */
		Mapy.getComponent("logger").log(name, {
			position: "top",
			source: this._logSource,
			section: "reviews",
			rating_layout: getFirmLayout(this._data.review, this._data.booking, this._data.myReview, this._data.isFirm),
			type: "recenze",
			sourcePoi: this._data.source,
			typeid: this._data.typeId,
			firmTwin: this._data.isTwin ? 1 : 0,
			...data,
		});
	}
}
